import { Button, Row } from "antd";
import LoadingSkeleton from "../../../LoadingSkeleton";
import TreeList, { Column, Scrolling, Selection, SearchPanel as TreeSearch } from "devextreme-react/tree-list";
import { Col } from "react-bootstrap";
import { useDepartment } from "../../statistics/hooks/use-department";
import { useEmployeeByDepartment } from "../../statistics/hooks/use-employee-by-department";
import { useQueryClient } from "@tanstack/react-query";
import { FontFa } from "../../../components";
import { useState } from "react";
import { useSearchParams } from "../../../hook/use-search-params";
import { useDocumentContentVersion } from "../hooks/document-content/use-document-content-version";
import { Employee } from "../../../services/department-viags/employee-viags.service";
import { CreateNotification } from "../../../components/notifier";
import { useEditorContentPermission } from "../editor-content-permission-context";
import DataGrid, { FilterRow, HeaderFilter, SearchPanel } from "devextreme-react/data-grid";
import { useUpdateDocumentContent } from "../hooks/content-mutation/use-update-document-content";

export const EditorContentPermissionModalForm = (props: any) => {
  const { data: departmentData, isLoading: isDepartmentDataLoading } = useDepartment();
  const { data: employeeData } = useEmployeeByDepartment();
  const { data, selectedPermisison } = useDocumentContentVersion();
  const queryClient = useQueryClient();
  const { searchParams, replaceSearchParams } = useSearchParams();
  const { setContentPermission, contentPermissions } = useEditorContentPermission();
  const { customId, setSelectedDepartment, onClose } = props;
  const [selectedEmployee, setSelectedEmployee] = useState<Employee[]>();
  const [currentEmployeeAfterDeselect, setCurrentEmployeeAfterDeselect] = useState<Employee[]>();

  const granted = searchParams.get("granted");
  const { mutate } = useUpdateDocumentContent();

  const handleCellClick = async (e: any) => {
    setSelectedDepartment(e?.data);
    setSelectedEmployee([]);
    const treeList = e.component;
    const targetRowKey = e.key;
    treeList.expandRow(targetRowKey);
    const departmentCode = e?.data?.code;
    searchParams.set("deptCode", departmentCode);
    searchParams.delete("granted");
    replaceSearchParams(searchParams);
  };

  const handleNameRender = (e: any) => {
    return (
      <div style={{ cursor: "pointer" }}>
        <span className="mr-1 text-warning">
          {" "}
          <FontFa name="faBuilding" size={"xs"} />
        </span>
        <span>{e.data.name}</span>
      </div>
    );
  };

  const handleTextChange = async (e: any) => {
    searchParams.set("deptCode", "VIAGS");
    replaceSearchParams(searchParams);
  };

  const handleSelectEmployee = (e: { selectedRowsData: Employee[]; currentDeselectedRowKeys: Employee[] }) => {
    if (!e.selectedRowsData) return;
    const selectedRowsData = [...e.selectedRowsData];
    const deselectedData = [...e.currentDeselectedRowKeys];
    if (deselectedData.length > 0) {
      setSelectedEmployee(selectedRowsData);
      setCurrentEmployeeAfterDeselect(selectedRowsData);
    } else {
      setSelectedEmployee(selectedRowsData);
    }
  };

  const handleConfirm = () => {
    if (data?.length > 1) {
      const currentContent = data[data.length - 1];
      const oldPermission = !selectedPermisison ? [] : [...selectedPermisison];
      const currentPermission = !selectedEmployee ? [] : [...selectedEmployee];
      const newPermission = [...oldPermission, ...currentPermission];
      const distinctObjects = Array.from(new Set(newPermission.map((obj) => obj.employeeCode))).map((employeeCode) => {
        return newPermission.find((obj) => obj.employeeCode === employeeCode);
      });
      const fields = {
        Permissions: JSON.stringify([...new Set(distinctObjects)]),
      };
      mutate(
        { id: currentContent.id, body: fields },
        {
          onSuccess() {
            queryClient.invalidateQueries({ queryKey: ["document-content-version"] });
            CreateNotification({
              title: "Thông báo",
              body: "Phân quyền trên đoạn văn bản thành công !",
              icon: "/icons/success.png",
            });
          },
        },
      );
    } else {
      const clonedArr = [...contentPermissions];
      const fields = {
        customId: customId,
        permissions: JSON.stringify(selectedEmployee),
      };
      const index = clonedArr.findIndex((e) => e.customId === fields.customId);
      if (index > -1) clonedArr[index] = fields;
      else clonedArr.push(fields);
      setContentPermission(clonedArr);
      CreateNotification({
        title: "Thông báo",
        body: "Phân quyền trên đoạn văn bản thành công !",
        icon: "/icons/success.png",
      });
    }

    onClose();
  };

  const handleUpdate = () => {
    if (data?.length > 1) {
      const currentContent = data[data.length - 1];

      const fields = {
        Permissions: JSON.stringify([...new Set(currentEmployeeAfterDeselect)]),
      };

      mutate(
        { id: currentContent.id, body: fields },
        {
          onSuccess() {
            queryClient.invalidateQueries({ queryKey: ["document-content-version"] });
            CreateNotification({
              title: "Thông báo",
              body: "Cập nhật phân quyền trên đoạn văn bản thành công !",
              icon: "/icons/success.png",
            });
          },
        },
      );
    }
    onClose();
  };

  const handleViewGrantedPermissionClick = () => {
    setSelectedEmployee(selectedPermisison);
    searchParams.set("granted", "true");
    replaceSearchParams(searchParams);
  };

  return (
    <>
      <Row>
        {isDepartmentDataLoading ? (
          <LoadingSkeleton />
        ) : (
          <Col sm="3" md="3">
            <TreeList
              id="Folder"
              dataSource={departmentData}
              showRowLines={false}
              showBorders={false}
              columnAutoWidth={true}
              allowColumnReordering={false}
              itemsExpr="viagsDepartmentChild"
              dataStructure="tree"
              expandNodesOnFiltering={false}
              autoExpandAll={false}
              onCellClick={handleCellClick}
              allowColumnResizing={false}
              height={800}
            >
              <Scrolling mode="standard" />
              <Selection mode="single" />
              <TreeSearch visible={true} placeholder="Nhập tên phòng ban" width={300} />
              <Selection allowSelectAll={false} mode="single" />
              <Column
                dataField="name"
                caption="Phòng/Ban"
                key="id"
                cellRender={handleNameRender}
                allowReordering={false}
                allowSorting={false}
              />
            </TreeList>
          </Col>
        )}
        <Col sm="9" md="9">
          <Button
            style={{ position: "absolute", top: "4px", left: "24px", zIndex: "9999" }}
            type="primary"
            onClick={handleViewGrantedPermissionClick}
          >
            Danh sách đã phân quyền
          </Button>
          <DataGrid
            id="employeeEmail"
            dataSource={granted ? selectedEmployee : employeeData}
            showRowLines={true}
            showBorders={true}
            columnAutoWidth={true}
            className="px-2"
            allowColumnReordering={false}
            allowColumnResizing={false}
            onSelectionChanged={handleSelectEmployee}
            selectedRowKeys={selectedEmployee}
            noDataText="Anh/Chị chưa chọn phòng ban hoặc phòng ban chưa có nhân sự nào"
          >
            <HeaderFilter visible={true} />
            <FilterRow visible={true} applyFilter={true} />
            <SearchPanel
              visible={true}
              placeholder="Nhập tên nhân sự cần tìm"
              width={400}
              onTextChange={handleTextChange}
            />
            <Selection mode="multiple" allowSelectAll={true} />
            <Column dataField="employeeCode" caption="Mã nhân sự" dataType="string"></Column>
            <Column dataField="employeeName" caption="Tên nhân sự"></Column>
            <Column dataField="employeeEmail" caption="Email"></Column>
          </DataGrid>
        </Col>
      </Row>
      <div className="d-flex flex-row-reverse mt-2" style={{ marginRight: "22px" }}>
        {granted ? (
          <Button type="primary" onClick={handleUpdate}>
            Cập nhật
          </Button>
        ) : (
          <Button type="primary" onClick={handleConfirm}>
            Xác nhận
          </Button>
        )}
        <Button type="ghost" onClick={() => onClose()} className="mr-1">
          Huỷ
        </Button>
      </div>
    </>
  );
};
